/* eslint-disable react/no-array-index-key */
import PropTypes from 'prop-types';
import { m } from 'framer-motion';
import {
    Box, Typography, Stack, Divider, alpha,
} from '@mui/material';
import Iconify from 'src/components/Iconify';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(val) {
    const n = parseFloat(val);
    if (Number.isNaN(n) || n <= 0) return null;
    return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function computePackageTotal(pkg) {
    // Support both costItems (from wizard) and treatmentItems (legacy)
    const items = pkg.costItems || pkg.treatmentItems || [];
    if (items.length > 0) {
        return items.reduce((sum, item) => {
            const qty = parseFloat(item.qty) || 0;
            const unitPrice = parseFloat(item.unitPrice) || 0;
            return sum + qty * unitPrice;
        }, 0);
    }
    return 0;
}

function resolvePaymentPlanText(paymentPlan) {
    if (!paymentPlan) return '';
    if (typeof paymentPlan === 'string') return paymentPlan;
    // paymentPlan is an object { type, details }
    if (paymentPlan.type === 'no' || !paymentPlan.type) return '';
    if (paymentPlan.details) return paymentPlan.details;
    if (paymentPlan.type === 'monthly') return 'Monthly instalments';
    if (paymentPlan.type === 'external') return 'Finance arranged externally';
    return '';
}

// ─── PlanPhaseTimeline ───────────────────────────────────────────────────────

function PlanPhaseTimeline({ phases }) {
    if (!phases || phases.length === 0) return null;

    return (
        <m.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
        >
            <Box sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 1.5, display: 'block' }}>
                    Treatment timeline
                </Typography>

                <Stack spacing={1}>
                    {phases.map((phase, idx) => (
                        <Stack
                            key={phase.id || idx}
                            direction="row"
                            alignItems="flex-start"
                            spacing={1.5}
                            sx={{
                                p: 1.25,
                                borderRadius: '8px',
                                bgcolor: alpha('#00AB55', 0.04),
                            }}
                        >
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: '#00AB55',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    mt: 0.25,
                                }}
                            >
                                <Typography variant="caption" sx={{ fontSize: '12px', fontWeight: 700, color: '#fff' }}>
                                    {idx + 1}
                                </Typography>
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#212B36', fontSize: '13px' }}>
                                    {phase.name || `Phase ${idx + 1}`}
                                </Typography>
                                {phase.duration && (
                                    <Typography variant="caption" sx={{ fontSize: '12px', color: '#919EAB', mt: 0.25, display: 'block' }}>
                                        {phase.duration}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    ))}
                </Stack>

                <Divider sx={{ mt: 2.5, mb: 0 }} />
            </Box>
        </m.div>
    );
}

PlanPhaseTimeline.propTypes = {
    phases: PropTypes.array,
};

// ─── PackagePreview ──────────────────────────────────────────────────────────

function PackagePreview({ pkg, idx, totalPackages }) {
    const totalCost = computePackageTotal(pkg);
    const totalStr = totalCost > 0 ? formatCurrency(totalCost) : null;
    const deposit = pkg.deposit ? parseFloat(pkg.deposit) : 0;
    const phases = pkg.phases || [];
    const lineItems = pkg.costItems || pkg.treatmentItems || [];
    const paymentPlanText = resolvePaymentPlanText(pkg.paymentPlan);

    const isMultiPackage = totalPackages > 1;
    const packageLabel = isMultiPackage ? `Option ${String.fromCharCode(65 + idx)}` : 'Your Treatment Plan';

    return (
        <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + idx * 0.1, duration: 0.5 }}
        >
            <Box
                sx={{
                    borderRadius: '12px',
                    border: '1px solid rgba(145,158,171,0.12)',
                    bgcolor: '#fff',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    },
                }}
            >
                {/* Package header with gradient */}
                <Box
                    sx={{
                        px: 2.5, py: 2.25,
                        background: `linear-gradient(135deg, ${alpha('#00AB55', 0.08)} 0%, ${alpha('#00AB55', 0.02)} 100%)`,
                        borderBottom: '1px solid rgba(145,158,171,0.08)',
                    }}
                >
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                        <Box>
                            <Typography variant="caption" sx={{ fontSize: '10px', fontWeight: 700, color: '#00AB55', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                {packageLabel}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px', color: '#212B36', mt: 0.5 }}>
                                {pkg.name || `Package ${idx + 1}`}
                            </Typography>
                        </Box>
                        {totalStr && (
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" sx={{ fontSize: '11px', color: '#919EAB', display: 'block', mb: 0.25 }}>
                                    Total cost
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '22px', color: '#00AB55', fontVariantNumeric: 'tabular-nums' }}>
                                    {totalStr}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>

                <Box sx={{ px: 2.5, py: 2.25 }}>
                    {/* Quick stats */}
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        {pkg.duration && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Iconify icon="eva:clock-outline" width={14} sx={{ color: '#919EAB' }} />
                                <Typography variant="caption" sx={{ fontSize: '13px', color: '#637381' }}>
                                    {pkg.duration}
                                </Typography>
                            </Stack>
                        )}
                        {pkg.appointments && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Iconify icon="eva:calendar-outline" width={14} sx={{ color: '#919EAB' }} />
                                <Typography variant="caption" sx={{ fontSize: '13px', color: '#637381' }}>
                                    {pkg.appointments} appointment{parseInt(pkg.appointments, 10) !== 1 ? 's' : ''}
                                </Typography>
                            </Stack>
                        )}
                        {deposit > 0 && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Iconify icon="eva:credit-card-outline" width={14} sx={{ color: '#919EAB' }} />
                                <Typography variant="caption" sx={{ fontSize: '13px', color: '#637381' }}>
                                    {formatCurrency(deposit)} deposit
                                </Typography>
                            </Stack>
                        )}
                    </Stack>

                    {/* Description */}
                    {pkg.description && (
                        <Box sx={{ mb: 2.25 }}>
                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#637381', lineHeight: 1.7 }}>
                                {pkg.description}
                            </Typography>
                        </Box>
                    )}

                    {/* Treatment line items table */}
                    {lineItems.length > 0 && (
                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 1.25, display: 'block' }}>
                                Treatment breakdown
                            </Typography>

                            {/* Table header */}
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{
                                    px: 1.5, py: 1,
                                    bgcolor: alpha('#212B36', 0.03),
                                    borderRadius: '6px 6px 0 0',
                                    borderBottom: '1px solid rgba(145,158,171,0.12)',
                                }}
                            >
                                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 700, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    Treatment
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 700, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    Cost
                                </Typography>
                            </Stack>

                            {/* Table rows */}
                            {lineItems.map((item, i) => {
                                const qty = parseFloat(item.qty) || 0;
                                const unitPrice = parseFloat(item.unitPrice) || 0;
                                const lineCost = qty * unitPrice;
                                const isLastRow = i === lineItems.length - 1;

                                return (
                                    <Stack
                                        key={item.id || i}
                                        direction="row"
                                        justifyContent="space-between"
                                        sx={{
                                            px: 1.5, py: 1.25,
                                            bgcolor: i % 2 === 0 ? '#fff' : alpha('#212B36', 0.01),
                                            borderBottom: !isLastRow ? '1px solid rgba(145,158,171,0.06)' : 'none',
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#212B36', fontWeight: 500 }}>
                                                {item.description || 'Treatment item'}
                                            </Typography>
                                            {qty > 1 && (
                                                <Typography variant="caption" sx={{ fontSize: '11px', color: '#919EAB', mt: 0.25, display: 'block' }}>
                                                    Qty: {qty}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: '#212B36',
                                                fontVariantNumeric: 'tabular-nums',
                                                ml: 2,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {lineCost > 0 ? formatCurrency(lineCost) : '—'}
                                        </Typography>
                                    </Stack>
                                );
                            })}

                            {/* Total row */}
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{
                                    px: 1.5, py: 1.5,
                                    bgcolor: alpha('#00AB55', 0.06),
                                    borderRadius: '0 0 6px 6px',
                                    borderTop: '2px solid rgba(0,171,85,0.2)',
                                }}
                            >
                                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 700, color: '#212B36' }}>
                                    Total for treatments
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 800, color: '#00AB55', fontVariantNumeric: 'tabular-nums' }}>
                                    {totalStr || '—'}
                                </Typography>
                            </Stack>
                        </Box>
                    )}

                    {/* Deposit & payment plan */}
                    {(deposit > 0 || paymentPlanText) && (
                        <Box sx={{ mb: 2.5 }}>
                            {deposit > 0 && (
                                <Box sx={{ mb: paymentPlanText ? 1.5 : 0 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.75, display: 'block' }}>
                                        Deposit required
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.25, bgcolor: alpha('#00AB55', 0.04), borderRadius: '8px' }}>
                                        <Iconify icon="eva:credit-card-outline" width={16} sx={{ color: '#00AB55', flexShrink: 0 }} />
                                        <Typography variant="body2" sx={{ fontSize: '13px', color: '#212B36', fontWeight: 600 }}>
                                            {formatCurrency(deposit)} upfront
                                        </Typography>
                                    </Stack>
                                </Box>
                            )}

                            {paymentPlanText && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.75, display: 'block' }}>
                                        Payment plan
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#637381', lineHeight: 1.6 }}>
                                        {paymentPlanText}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Package-level phases */}
                    {phases.length > 0 && (
                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 1.25, display: 'block' }}>
                                Treatment phases for this package
                            </Typography>

                            <Stack spacing={0.75}>
                                {phases.map((phase, i) => (
                                    <Stack
                                        key={phase.id || i}
                                        direction="row"
                                        alignItems="flex-start"
                                        spacing={1}
                                        sx={{ py: 0.75, pl: 0.5 }}
                                    >
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#00AB55', flexShrink: 0, mt: 0.75 }} />
                                        <Box>
                                            <Typography variant="caption" sx={{ fontSize: '13px', color: '#212B36', fontWeight: 600, display: 'block' }}>
                                                {phase.name || `Phase ${i + 1}`}
                                            </Typography>
                                            {phase.duration && (
                                                <Typography variant="caption" sx={{ fontSize: '12px', color: '#919EAB', display: 'block', mt: 0.25 }}>
                                                    {phase.duration}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Included / Excluded sections */}
                    {(pkg.included || pkg.excluded) && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            {pkg.included && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#00AB55', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.75, display: 'block' }}>
                                        Included
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '12px', color: '#637381', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                                        {pkg.included}
                                    </Typography>
                                </Box>
                            )}
                            {pkg.excluded && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#FF4842', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.75, display: 'block' }}>
                                        Not included
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '12px', color: '#637381', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                                        {pkg.excluded}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </m.div>
    );
}

PackagePreview.propTypes = {
    pkg: PropTypes.object.isRequired,
    idx: PropTypes.number.isRequired,
    totalPackages: PropTypes.number.isRequired,
};

// ─── Main export ─────────────────────────────────────────────────────────────

export default function TreatmentPlanPreview({
    planName,
    options,
    planFields,
    thumbnailUrl,
    patientNotes,
    patientName,
    phases,
}) {
    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Iconify icon="eva:eye-fill" width={20} sx={{ color: '#00AB55' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px' }}>
                        Preview
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                    This is how the patient will see the treatment plan
                </Typography>
            </Box>

            {/* Preview card — simulates patient view */}
            <m.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
            >
                <Box
                    sx={{
                        maxWidth: 680,
                        mx: 'auto',
                        borderRadius: '16px',
                        border: '2px solid rgba(145,158,171,0.12)',
                        bgcolor: '#fff',
                        overflow: 'hidden',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    }}
                >
                    {/* Hero image */}
                    {thumbnailUrl && (
                        <Box
                            sx={{
                                width: '100%',
                                maxHeight: 200,
                                overflow: 'hidden',
                                bgcolor: '#F4F6F8',
                            }}
                        >
                            <Box
                                component="img"
                                src={thumbnailUrl}
                                alt="Treatment plan"
                                onError={(e) => { e.target.style.display = 'none'; }}
                                sx={{ width: '100%', maxHeight: 200, objectFit: 'cover' }}
                            />
                        </Box>
                    )}

                    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                        {/* Plan title */}
                        <m.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05, duration: 0.4 }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '24px', color: '#212B36', mb: 0.5 }}>
                                {planName || 'Treatment Plan'}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '14px', color: '#919EAB', mb: 2.5 }}>
                                Prepared for {patientName || 'Patient'}
                            </Typography>
                        </m.div>

                        {/* Patient note */}
                        {patientNotes && (
                            <m.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.06, duration: 0.4 }}
                            >
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: '10px',
                                        bgcolor: alpha('#078DEE', 0.04),
                                        border: `1px solid ${alpha('#078DEE', 0.12)}`,
                                        mb: 2.5,
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <Iconify icon="eva:message-square-fill" width={16} sx={{ color: '#078DEE', mt: 0.25, flexShrink: 0 }} />
                                        <Typography variant="body2" sx={{ fontSize: '13px', color: '#637381', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                                            {patientNotes}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </m.div>
                        )}

                        {/* Plan-level context */}
                        {planFields?.reason && (
                            <m.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.07, duration: 0.4 }}
                            >
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.75, display: 'block' }}>
                                        Why we recommend this
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#637381', lineHeight: 1.7 }}>
                                        {planFields.reason}
                                    </Typography>
                                </Box>
                            </m.div>
                        )}

                        {planFields?.plainEnglish && (
                            <m.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.08, duration: 0.4 }}
                            >
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.75, display: 'block' }}>
                                        In plain terms
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#637381', lineHeight: 1.7 }}>
                                        {planFields.plainEnglish}
                                    </Typography>
                                </Box>
                            </m.div>
                        )}

                        {(planFields?.reason || planFields?.plainEnglish) && <Divider sx={{ mb: 2.5 }} />}

                        {/* Plan-level phases */}
                        <PlanPhaseTimeline phases={phases} />

                        {/* Package comparison heading */}
                        {options.length > 1 && (
                            <m.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.12, duration: 0.4 }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '15px', color: '#212B36', mb: 2.5, textAlign: 'center' }}>
                                    Compare your treatment packages
                                </Typography>
                            </m.div>
                        )}

                        {/* Packages */}
                        <Stack spacing={2.5}>
                            {options.map((opt, idx) => (
                                <PackagePreview key={opt.id || idx} pkg={opt} idx={idx} totalPackages={options.length} />
                            ))}
                        </Stack>

                        {/* Signature placeholder */}
                        <m.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 + options.length * 0.1, duration: 0.4 }}
                        >
                            <Box
                                sx={{
                                    mt: 4,
                                    pt: 3,
                                    borderTop: '1px solid rgba(145,158,171,0.12)',
                                    textAlign: 'center',
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mb: 1.5 }}>
                                    <Iconify icon="eva:edit-2-fill" width={15} sx={{ color: '#919EAB' }} />
                                    <Typography variant="caption" sx={{ fontSize: '12px', color: '#919EAB', fontWeight: 600 }}>
                                        Digital signature pad will appear here
                                    </Typography>
                                </Stack>
                                <Box
                                    sx={{
                                        height: 80,
                                        borderRadius: '10px',
                                        border: '2px dashed rgba(145,158,171,0.2)',
                                        bgcolor: '#FAFBFC',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontSize: '13px', color: '#C4CDD5', fontWeight: 500 }}>
                                        Patient signs here to accept
                                    </Typography>
                                </Box>
                            </Box>
                        </m.div>
                    </Box>
                </Box>
            </m.div>
        </Box>
    );
}

TreatmentPlanPreview.propTypes = {
    planName: PropTypes.string,
    options: PropTypes.array.isRequired,
    planFields: PropTypes.object,
    thumbnailUrl: PropTypes.string,
    patientNotes: PropTypes.string,
    patientName: PropTypes.string,
    phases: PropTypes.array,
};

TreatmentPlanPreview.defaultProps = {
    planName: '',
    planFields: null,
    thumbnailUrl: '',
    patientNotes: '',
    patientName: 'Patient',
    phases: [],
};
